import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { creatorAPI } from '../../../api';
import { areaByKey, EVO_META } from '../constants';

const monthShift = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (ym, locale) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
};

// Global Timeline — "what was my head living on this month?"
// Builds itself from every item's append-only Evolution.
export default function TimelineView({ t, locale, onBack }) {
  const navigate = useNavigate();
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    creatorAPI.timeline(month || undefined)
      .then(({ data: d }) => { setData(d); if (!month) setMonth(d.month); })
      .catch(() => setData({ events: [], area_counts: {} }));
  }, [month]);

  const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const events = data?.events || [];

  const byDay = [];
  let lastDay = null;
  for (const e of events) {
    const day = (e.at || '').slice(0, 10);
    if (day !== lastDay) {
      byDay.push({ day, items: [] });
      lastDay = day;
    }
    byDay[byDay.length - 1].items.push(e);
  }

  const areaSummary = Object.entries(data?.area_counts || {})
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${areaByKey(k)?.name || k} ${v}`)
    .join(' \u00b7 ');

  return (
    <div className="crs-areaview" data-testid="creator-timeline-view">
      <button type="button" className="crs-backlink" onClick={onBack} data-testid="creator-timeline-back">
        ← {t.cr_title || 'Creator Space'}
      </button>
      <div className="crs-areaview__head">
        <h2>{t.cr_your_timeline || 'Your timeline'}</h2>
        <nav className="crs-monthnav" data-testid="creator-timeline-monthnav">
          <button type="button" onClick={() => setMonth(monthShift(month || currentMonth, -1))} data-testid="creator-timeline-prev">‹</button>
          <span>{month ? monthLabel(month, locale) : ''}</span>
          <button
            type="button"
            onClick={() => setMonth(monthShift(month || currentMonth, 1))}
            disabled={(month || currentMonth) >= currentMonth}
            data-testid="creator-timeline-next"
          >›</button>
        </nav>
      </div>

      {data === null ? (
        <div className="spinner" style={{ margin: '48px auto' }} />
      ) : (
        <>
          {areaSummary && (
            <p className="crs-timeline__summary" data-testid="creator-timeline-summary">{areaSummary}</p>
          )}
          {events.length === 0 ? (
            <p className="crs-empty" data-testid="creator-timeline-empty">{t.cr_no_timeline || 'A quiet month \u2014 nothing captured here.'}</p>
          ) : (
            byDay.map(({ day, items }) => (
              <section key={day} className="crs-timeline__day">
                <p className="crs-section__label">
                  {new Date(`${day}T12:00:00`).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                {items.map((e, i) => {
                  const meta = EVO_META[e.kind] || EVO_META.note;
                  return (
                    <button
                      key={`${e.item_id}-${e.at}-${i}`}
                      type="button"
                      className="crs-timeline__event"
                      onClick={() => navigate(`/creator/item/${e.item_id}`)}
                    >
                      <span className="crs-timeline__time">
                        {new Date(e.at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className={`crs-evo__symbol is-${e.kind}`}>{meta.symbol}</span>
                      <span className="crs-timeline__body">
                        <em>{t[meta.labelKey] || e.kind}</em>
                        {' \u2014 '}
                        {e.kind === 'created' ? e.item_title : (e.text || e.item_title)}
                        {e.kind !== 'created' && e.text && e.text !== e.item_title && (
                          <span className="crs-timeline__source"> · {e.item_title}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </section>
            ))
          )}
        </>
      )}
    </div>
  );
}
